import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #availabilityObserver;

  #availabilityUpdateQueued = false;

  #availabilityRevision = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeAvailabilityChanges();
    this.#queueAvailabilityUpdate();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#availabilityObserver?.disconnect();
    this.#availabilityRevision += 1;
  }

  #observeAvailabilityChanges() {
    if (this.getAttribute('availability-order') !== 'available-first') return;

    if (!this.#availabilityObserver) {
      this.#availabilityObserver = new MutationObserver((mutations) => {
        const grid = this.refs.grid;
        const gridChanged = mutations.some((mutation) => {
          if (mutation.type === 'attributes' || mutation.target === grid) return true;

          return [...mutation.addedNodes, ...mutation.removedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches('[ref="grid"], [ref="cards[]"]') ||
                node.querySelector('[ref="grid"], [ref="cards[]"]'))
          );
        });

        if (gridChanged) this.#queueAvailabilityUpdate();
      });
    }
    this.#availabilityObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
      subtree: true,
    });
  }

  #queueAvailabilityUpdate() {
    if (this.getAttribute('availability-order') !== 'available-first' || this.#availabilityUpdateQueued) return;

    this.#availabilityUpdateQueued = true;
    const revision = ++this.#availabilityRevision;

    queueMicrotask(() => {
      this.#availabilityUpdateQueued = false;
      void this.#loadAndOrderCards(revision);
    });
  }

  /**
   * Loads every paginated result before ordering when infinite scroll is on.
   * Shopify paginates products, while this theme can render one card per color,
   * so a complete availability order cannot be calculated from one page alone.
   *
   * @param {number} revision
   */
  async #loadAndOrderCards(revision) {
    const { grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage || 1);
    const shouldLoadAllPages = this.managesAvailabilityPagination && lastPage > 1;
    const requestUrl = new URL(window.location.href);
    const requestKey = `${requestUrl.pathname}${requestUrl.search}`;

    if (shouldLoadAllPages) {
      grid.setAttribute('aria-busy', 'true');

      const loadedPages = new Set(
        this.#getAvailabilityCards(grid)
          .map((card) => Number(card.dataset.page))
          .filter(Number.isFinite)
      );
      const missingPages = Array.from({ length: lastPage }, (_, index) => index + 1).filter(
        (page) => !loadedPages.has(page)
      );
      const fetchedPages = [];

      try {
        for (let index = 0; index < missingPages.length; index += 3) {
          const pageBatch = missingPages.slice(index, index + 3);
          const batch = await Promise.all(
            pageBatch.map(async (page) => {
              const pageUrl = new URL(requestUrl);
              pageUrl.searchParams.set('page', page.toString());
              pageUrl.hash = '';

              const html = await sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);
              return { page, html };
            })
          );

          fetchedPages.push(...batch);

          if (!this.#isCurrentAvailabilityRequest(revision, grid, requestKey)) return;
        }

        this.#availabilityObserver?.disconnect();
        this.#appendFetchedCards(grid, fetchedPages);
        this.#orderCards(grid);
      } catch (error) {
        console.error('Unable to order all product cards by availability.', error);
        this.#orderCards(grid);
      } finally {
        if (this.refs.grid === grid) grid.removeAttribute('aria-busy');
        if (this.isConnected) this.#observeAvailabilityChanges();
      }
    } else {
      this.#availabilityObserver?.disconnect();
      this.#orderCards(grid);
      if (this.isConnected) this.#observeAvailabilityChanges();
    }
  }

  /**
   * @param {number} revision
   * @param {HTMLElement} grid
   * @param {string} requestKey
   */
  #isCurrentAvailabilityRequest(revision, grid, requestKey) {
    const currentUrl = new URL(window.location.href);
    const currentKey = `${currentUrl.pathname}${currentUrl.search}`;
    return revision === this.#availabilityRevision && this.refs.grid === grid && requestKey === currentKey;
  }

  /**
   * @param {HTMLElement} grid
   * @param {{ page: number, html: string }[]} fetchedPages
   */
  #appendFetchedCards(grid, fetchedPages) {
    const knownIds = new Set(this.#getAvailabilityCards(grid).map((card) => card.id));
    const fragment = document.createDocumentFragment();

    for (const { page, html } of fetchedPages.sort((a, b) => a.page - b.page)) {
      const parsedPage = new DOMParser().parseFromString(html, 'text/html');
      const pageGrid = parsedPage.querySelector('[ref="grid"]');
      if (!pageGrid) continue;

      const pageCards = pageGrid.querySelectorAll(':scope > [ref="cards[]"][data-card-available]');
      for (const card of pageCards) {
        if (!(card instanceof HTMLElement) || knownIds.has(card.id)) continue;
        knownIds.add(card.id);
        fragment.append(card);
      }
    }

    grid.append(fragment);
  }

  /**
   * Stable partition: available cards first, sold-out cards second. Page and
   * card position preserve the exact source order inside both groups.
   *
   * @param {HTMLElement} grid
   */
  #orderCards(grid) {
    const cards = this.#getAvailabilityCards(grid);
    const orderedCards = [...cards].sort((a, b) => {
      const availabilityDifference =
        Number(b.dataset.cardAvailable === 'true') - Number(a.dataset.cardAvailable === 'true');
      if (availabilityDifference !== 0) return availabilityDifference;

      const pageDifference = Number(a.dataset.page) - Number(b.dataset.page);
      if (pageDifference !== 0) return pageDifference;

      return Number(a.dataset.cardPosition) - Number(b.dataset.cardPosition);
    });

    if (orderedCards.every((card, index) => card === cards[index])) return;
    grid.append(...orderedCards);
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  #getAvailabilityCards(grid) {
    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"][data-card-available]')).filter(
      (card) => card instanceof HTMLElement
    );
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
